import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject, interval, of, switchMap, take, finalize } from 'rxjs';
import { isNil } from 'lodash';
import { catchError, map, takeUntil, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { AuthenticatedUser, EmailVerification, EmailVerificationData, OIDCType, RegistrationData } from '../../common/models';
import { ConfirmOtp } from '../../pages/registration/models';
import { Router } from '@angular/router';
import { UiNotificationService } from '../../ui/ui-notification/ui-notification.service';
import { TranslocoService } from '@ngneat/transloco';
import { OidcStatus, oidcStorageKey, oidcStorageStatusKey } from '../../pages/login/models';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  private readonly http = inject(HttpClient);
  private readonly translocoService = inject(TranslocoService);
  private readonly notificationsService = inject(UiNotificationService);
  private readonly router = inject(Router);
  readonly POLL_INTERVAL = 1000;
  readonly domain = environment.domain;
  readonly authenticatedUserSource$ = new BehaviorSubject<AuthenticatedUser>(null);
  readonly authenticatedUser$ = this.authenticatedUserSource$.pipe(
    switchMap((data) => {
      if (!isNil(data)) {
        return of(data);
      }

      return this.whoami();
    })
  );
  readonly isAuthenticated$ = this.authenticatedUser$.pipe(map((user) => !isNil(user)));
  readonly oidcPoll$ = interval(this.POLL_INTERVAL);
  readonly stopPoll$ = new Subject<void>();

  readonly OIDCUrl: Record<OIDCType, string> = {
    [OIDCType.facebook]: '/v1/public/oidc/login/facebook',
    [OIDCType.google]: '/v1/public/oidc/login/google',
  };

  setNotAuthenticated(): void {
    // rm user from root store this.rootStoreService.addActiveUser(null);
    this.authenticatedUserSource$.next(null);
  }

  setUser(user: AuthenticatedUser): void {
    // add user to root store this.rootStoreService.addActiveUser(user) (or fetch);
    this.authenticatedUserSource$.next(user);
  }

  get isAuthenticated(): Observable<boolean> {
    return this.isAuthenticated$;
  }

  loginOIDC(type: OIDCType): void {
    const url = `${this.domain}${this.OIDCUrl[type]}`;

    // if (type === OIDCType.facebook) {
    //   this.subscribeFbOidc();
    // }
    this.openOIDCWindow(url);
  }

  getOIDCUrl(type: OIDCType): string {
    return `${this.domain}${this.OIDCUrl[type]}`;
  }
  get user(): Observable<AuthenticatedUser> {
    return this.authenticatedUser$;
  }

  whoami(): Observable<AuthenticatedUser> {
    return this.http.get<AuthenticatedUser>(`${this.domain}/v1/user/whoami`).pipe(
      tap((user) => {
        this.authenticatedUserSource$.next(user);
      }),
      catchError(() => of(null))
    );
  }

  login(email: string, password: string, captcha: string, totp?: string): Observable<AuthenticatedUser> {
    const data = {
      email,
      password,
      captcha,
      totp,
    };

    return this.http.post<AuthenticatedUser>(`${this.domain}/v1/public/auth/login`, data).pipe(tap((user) => this.setUser(user)));
  }

  register(data: RegistrationData): Observable<AuthenticatedUser> {
    return this.http
      .post<AuthenticatedUser>(`${this.domain}/v2/public/auth/signup`, data)
      .pipe(tap((user) => this.authenticatedUserSource$.next(user)));
  }

  forgotPassword(data: EmailVerificationData): Observable<unknown> {
    return this.http.post<EmailVerification>(`${this.domain}/v2/public/auth/forgotpassword`, data);
  }

  confirmPassword(vid: string, vtoken: string, password: string, captcha: string): Observable<unknown> {
    return this.http.post<unknown>(`${this.domain}/v1/public/auth/forgotpassword/confirmation`, {
      vid,
      vtoken,
      password,
      captcha,
    });
  }

  confirmEmail(email: string, confirmationToken: string): Observable<unknown> {
    return this.http.post<void>(`${this.domain}/v2/public/auth/signup/confirmation`, {
      email,
      confirmationToken,
    });
  }

  logout(): Observable<unknown> {
    return this.http.post(`${this.domain}/v1/user/auth/logout`, null).pipe(finalize(() => this.setNotAuthenticated()));
  }

  resendCode(email: string, captcha: string): Observable<Record<string, string>> {
    const data = {
      email,
      captcha,
    };
    return this.http.post<Record<string, string>>(`${this.domain}/v2/public/auth/signup/confirmation/resend`, data);
  }

  confirmOtp(data: { vtoken: string; vid: string }): Observable<ConfirmOtp> {
    return this.http.post<ConfirmOtp>(`${this.domain}/v2/public/auth/signup/confirmation`, data);
  }

  private openOIDCWindow(url: string): Window | null {
    // const maxWidth= 700;
    // const height = window.innerHeight * 0.7;
    // const calcWidth = window.innerWidth * 0.7;
    // const width = calcWidth > maxWidth ? maxWidth : calcWidth;
    // const top = window.innerHeight - height;
    // const left = (window.innerWidth - width) / 2;

    return window.open(url, '_self');
  }

  private subscribeFbOidc(): void {
    this.oidcPoll$.pipe(takeUntil(this.stopPoll$)).subscribe(() => {
      const dialogStatus = localStorage.getItem(oidcStorageStatusKey);

      if (isNil(dialogStatus)) {
        return;
      }

      switch (dialogStatus) {
      case OidcStatus.error:
        this.notificationsService.showError(this.translocoService.translate('common.error.title'), localStorage.getItem(oidcStorageKey));
        this.stopOidcPoll();
        return;

      case OidcStatus.success:
        // this.notificationsService.showError(
        //     this.translocoService.translate('common.error.title'),
        //     dialogData
        // );
        this.stopOidcPoll();
        this.router.navigate(['cabinet']);
        return;

      default:
        // do nothing
      }
    });
  }

  private stopOidcPoll(): void {
    this.stopPoll$.next();
    localStorage.removeItem(oidcStorageKey);
  }
}
